import { z } from 'zod'
import { Result } from '@cplug/frontend-core/utils'
import { type DataWrappedDTO } from '@cplug/frontend-core/gateways'
import type { LoggedUser } from '../models/LoggedUser'
import { mapDTOToLoggedUser, mapLoggedUserToDTO, LoggedUserDTO } from './DTOs/LoggedUserDTO'
import type { UserPermission } from '~/domain/dashboard/user/permissions/models/UserPermision'
import {
  UserPermissionDTO,
  mapDTOToUserPermission
} from '~/domain/dashboard/user/permissions/gateways/DTOs/UserPermisionDTO'
import { CPBaseCRUDGateway } from '~/shared/gateways/CPBaseCRUDGateway'

type ListDTO = { users: LoggedUserDTO[] }

const LoggedUserPermissionsResponseDTO = z.object({
  data: z.object({
    permissions: UserPermissionDTO.array()
  }),
  meta: z.object({ total: z.number() }).optional()
})

export class CPLoggedUserGateway extends CPBaseCRUDGateway<LoggedUserDTO, ListDTO, LoggedUser> {
  protected apiUrlDomain = 'users/me'

  protected dtoToModelMapper = (dto: LoggedUserDTO): Result<LoggedUser> => Result.ofValue(mapDTOToLoggedUser(dto))
  protected modelToDtoMapper = mapLoggedUserToDTO
  protected dtoSchema = LoggedUserDTO

  protected get listField (): 'users' {
    return 'users'
  }

  protected override get modelIdField (): keyof LoggedUser {
    return 'id'
  }

  async getLoggedUser (): Promise<Result<LoggedUser>> {
    const response = await this.get<DataWrappedDTO<LoggedUserDTO>>(this.apiUrlDomain)

    return this.parseResponse(response, this.singleResponseDtoSchema, this.singleDtoToModelMapper.bind(this))
  }

  async getPermissions (): Promise<Result<UserPermission[]>> {
    const response = await this.get(`${this.apiUrlDomain}/permissions`)

    return this.parseResponse(response, LoggedUserPermissionsResponseDTO, (dto) => {
      return Result.ofValue(dto.data.permissions.map(permission => mapDTOToUserPermission(permission)))
    })
  }

  override async edit (model: LoggedUser): Promise<Result<LoggedUser>> {
    const data = this.modelToDtoMapper(model)

    const response = await this.put<DataWrappedDTO<LoggedUserDTO>>(this.apiUrlDomain, data)

    return this.parseSingleResponse(response, this.dtoSchema, this.dtoToModelMapper)
  }
}
